import React, { useState } from "react";
import { useSelector } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { getCookie } from '../../util.js';

import {
  Button,
  Table,
  toaster,
  Badge,
  Tooltip,
  Pane,
  IconButton,
  TrashIcon,
  RefreshIcon
} from 'evergreen-ui';

function GroupInvite(props) {
  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const thisinvite = props.publisherinvite;
  const [copied, setCopied] = useState(false);
  const [isResending, setIsResending] = useState(false);

  let baseUrl = process.env.REACT_APP_BASE_URL;
  const invitationUrl = baseUrl +
      'accept_invite?invite=' + thisinvite.invite.key +
      '&group=' + encodeURIComponent(currentpublisher.name) +
      '&pubid=' + currentpublisher.id.toString();

  // Get role name from either auth_group or role
  const getRoleName = () => {
    if (thisinvite.authGroupName) {
      return thisinvite.authGroupName;
    } else if (thisinvite.role) {
      return thisinvite.role.name;
    }
    return "Pending";
  };

  const handleCopy = () => {
    setCopied(true);
    toaster.success('Invitation URL copied to clipboard!');

    // Reset the copied state after 3 seconds
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  }

  const handleResendInvite = () => {
    setIsResending(true);
    const csrftoken = getCookie('csrftoken');

    // Determine if we should use auth_group or role
    const useAuthGroup = !!thisinvite.authGroupName;
    const roleId = useAuthGroup ? thisinvite.authGroupId : (thisinvite.role ? thisinvite.role.id : null);

    // Get the backend URL from environment variables with smart fallback
    let backendUrl = process.env.REACT_APP_BACKEND_URL;

    // If BACKEND_URL is not defined, determine it based on the current environment
    if (!backendUrl) {
      const currentEnv = process.env.REACT_APP_ENV || 'local';
      const currentUrl = window.location.origin;

      if (currentEnv === 'local') {
        // In local development, default to localhost:8000
        backendUrl = 'http://localhost:8000';
      } else {
        // In production or staging, use the same origin as the frontend
        backendUrl = currentUrl;
      }
      console.warn('REACT_APP_BACKEND_URL not found, using fallback:', backendUrl);
    }

    const url = `${backendUrl}/send_invitation/`;

    // Create the request payload
    const payload = {
      "email": thisinvite.invite.email,
      "publisher": currentpublisher.id,
      "role": roleId,
      "use_auth_group": useAuthGroup,
      "resend": true
    };

    console.log('Sending invitation request:', {
      url,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrftoken
      },
      payload
    });

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrftoken
      },
      body: JSON.stringify(payload)
    })
    .then(res => {
      console.log('Response status:', res.status);

      if (!res.ok) {
        // If response is not OK, try to parse error message or throw generic error
        return res.text().then(text => {
          console.log('Error response text:', text);
          try {
            const data = JSON.parse(text);
            throw new Error(data.message || `Server error: ${res.status}`);
          } catch (e) {
            throw new Error(`Server error: ${res.status}. Response: ${text}`);
          }
        });
      }
      return res.json();
    })
    .then(res => {
      console.log('Success response:', res);
      setIsResending(false);
      toaster.success('Invitation resent to ' + thisinvite.invite.email);
    })
    .catch(err => {
      console.error('Error resending invitation:', err);
      setIsResending(false);
      toaster.danger(err.message || 'Failed to resend invitation. Please try again.');
    });
  }

  return (
    <Table.Row className="table-row" value="">
      <Table.TextCell flexShrink={0} flexGrow={3}>{thisinvite.invite.email}</Table.TextCell>
      <Table.TextCell flexShrink={0} flexGrow={2}>
        {getRoleName()}
      </Table.TextCell>
      <Table.TextCell flexShrink={0} flexGrow={1}>
        <Badge color="neutral">Invited</Badge>
      </Table.TextCell>
      <Table.TextCell flexShrink={0} flexGrow={2}>
        <Pane display="flex">
          <IconButton
            icon={RefreshIcon}
            marginRight={8}
            intent="success"
            isLoading={isResending}
            onClick={props.hasAdminPrivileges ? handleResendInvite : undefined}
            disabled={!props.hasAdminPrivileges}
            title={props.hasAdminPrivileges ? "Resend invitation email" : "Only admins can resend invitations"}
          />

          <CopyToClipboard text={invitationUrl} onCopy={props.hasAdminPrivileges ? handleCopy : null}>
            <Button
              marginRight={8}
              appearance={copied ? "primary" : "default"}
              size="small"
              disabled={!props.hasAdminPrivileges}
              title={props.hasAdminPrivileges ? "Copy invitation link" : "Only admins can copy invitation links"}
            >
              {copied ? "Copied!" : "Copy Link"}
            </Button>
          </CopyToClipboard>

          <IconButton
            icon={TrashIcon}
            intent="danger"
            disabled={!props.hasAdminPrivileges}
            onClick={props.hasAdminPrivileges ? () => props.handleCancelInvite(thisinvite.id) : undefined}
            title={props.hasAdminPrivileges ? "Cancel invitation" : "Only admins can cancel invitations"}
          />
        </Pane>
      </Table.TextCell>
    </Table.Row>
  )
}

export default GroupInvite;
